"use client";

import {
  useEffect,
  useId,
  useRef,
  useState,
  useTransition,
  type FormEvent,
} from "react";
import { useRouter } from "next/navigation";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faGrid,
  faList,
  faMap,
  faRectangleList,
  faRows,
} from "@fortawesome/sharp-light-svg-icons";
import type { CaseData, CtaComponentProps } from "@/cms/models";
import RippleCtaButtonComponent from "@/components/ui/RippleCtaButtonComponent";
import RippleCtaButton from "@/components/ui/RippleCtaButton";
import SortCaseControl from "@/components/ui/SortCaseControl";
import SortingButton from "@/components/ui/SortingButton";
import ListFilterButton from "./ListFilterButton";
import { PropertyListCards, PropertyListMap } from "./PropertyListCards";
import {
  MAX_TAKE,
  countLine,
  listParamNames,
  type PropertyListSettings,
  type PropertyListView,
} from "./model";
import type { PropertyListParams } from "./query";
import { typeName, type PropertyListTypeButton } from "./type-filters";

export interface PropertyListInitial {
  items: CaseData[];
  total: number;
  params: PropertyListParams;
}
export interface ButtonLook {
  className: string;
  config: CtaComponentProps;
}
export interface PropertyListClientProps {
  sectionKey: string;
  ordinal: number;
  variant: number;
  /** false in the CMS preview and the guide: the controls are drawn but only the view switcher acts */
  interactive: boolean;
  /** true in the CMS preview and the guide, which never contact the map service: the maps are an illustration */
  mapIllustration?: boolean;
  status: "active" | "sold" | "rented" | "openhouse";
  list: PropertyListSettings;
  initial: PropertyListInitial;
  buttons: PropertyListTypeButton[];
  typeNames: Record<string, string>;
  /** »Hent flere« before the island has loaded: a link to the address with this list's take raised */
  moreHref?: string;
  loadMoreLook: ButtonLook;
  searchLook: ButtonLook;
  label: string;
  pill?: boolean;
  imageRatio?: string;
}

const VIEW_BUTTONS: Record<
  PropertyListView,
  { label: string; icon: typeof faGrid }
> = {
  grid: { label: "Gittervisning", icon: faGrid },
  list: { label: "Listevisning", icon: faList },
  row: { label: "Rækkevisning", icon: faRows },
  duo: { label: "Duovisning", icon: faRectangleList },
  map: { label: "Kortvisning", icon: faMap },
};

export default function PropertyListClient({
  sectionKey,
  ordinal,
  variant,
  interactive,
  mapIllustration = false,
  status,
  list,
  initial,
  buttons,
  typeNames,
  moreHref,
  loadMoreLook,
  searchLook,
  label,
  pill = false,
  imageRatio,
}: PropertyListClientProps) {
  const id = useId();
  const router = useRouter();
  const names = listParamNames(ordinal);
  const region = useRef<HTMLDivElement>(null);
  const searchField = useRef<HTMLInputElement>(null);
  const focusCard = useRef<number | null>(null);
  const focusAfterRender = useRef<"search" | "all" | null>(null);
  // The list's own choices are navigations, so what it shows is always the server render of the address: Back from a
  // case, a link to this page and a search from any search box all show what their address says, without a remount,
  // so focus and the map stay where they are.
  const { items, total, params } = initial;
  const [searched, setSearched] = useState(params.q);
  const [word, setWord] = useState(params.q ?? "");
  if (params.q !== searched) {
    setSearched(params.q);
    setWord(params.q ?? "");
  }
  const [view, setView] = useState<PropertyListView>(
    params.view ?? list.views[0],
  );
  const [control, setControl] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);
  const [navigating, startNavigation] = useTransition();
  const storageKey = () =>
    `reweb:property-list:${window.location.pathname}:${ordinal}`;

  useEffect(() => {
    setMounted(true);
    if (!list.rememberView || initial.params.view) return;
    try {
      const saved = window.localStorage.getItem(storageKey());
      if (
        saved &&
        (list.views.includes(saved as PropertyListView) ||
          (list.split && saved === "map"))
      )
        setView(saved as PropertyListView);
    } catch {
      /* Without storage the first view stays. */
    }
    // Read once after mount; the address's own view wins.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const target = focusAfterRender.current;
    if (!target) return;
    focusAfterRender.current = null;
    const all = region.current?.querySelector<HTMLElement>(
      '.pf-filters .pf-filter[aria-pressed="true"]',
    );
    if (target === "search" && searchField.current) searchField.current.focus();
    else if (target === "all" && all) all.focus();
    else region.current?.focus();
  }, [initial]);
  useEffect(() => {
    const index = focusCard.current;
    if (index === null) return;
    focusCard.current = null;
    region.current
      ?.querySelector<HTMLElement>(`[data-card-index="${index}"] a`)
      ?.focus();
  }, [items]);

  const addressWith = (changes: Record<string, string | undefined>) => {
    const url = new URL(window.location.href);
    for (const [name, value] of Object.entries(changes))
      if (value === undefined) url.searchParams.delete(name);
      else url.searchParams.set(name, value);
    return `${url.pathname}${url.search}`;
  };
  // Replace, not push, as on the classic list: Back leaves the list instead of undoing a type button.
  const navigate = (
    name: string,
    changes: Record<string, string | undefined>,
  ) => {
    if (navigating) return;
    setControl(name);
    startNavigation(() =>
      router.replace(addressWith(changes), { scroll: false }),
    );
  };
  const chooseFilter = (namespace: string | undefined, name: string) => {
    const filter =
      !namespace || namespace === "all" || namespace === params.filter
        ? undefined
        : namespace;
    navigate(name, { [names.filter]: filter, [names.take]: undefined });
  };
  const removeChip = () => {
    focusAfterRender.current = "all";
    chooseFilter(undefined, "chip");
  };
  const toggleOpenHouse = () =>
    navigate("openhouse", {
      [names.openhouse]: params.openhouse ? undefined : "1",
      [names.take]: undefined,
    });
  const chooseSort = (value: string) =>
    navigate("sort", {
      [names.sort]: value === list.defaultSort ? undefined : value,
    });
  const loadMore = () => {
    if (navigating) return;
    focusCard.current = items.length;
    navigate("more", {
      [names.take]: String(Math.min(MAX_TAKE, items.length + list.pageSize)),
    });
  };
  const search = (word: string) => {
    const address = addressWith({
      [names.q]: word || undefined,
      [names.take]: undefined,
    });
    setControl("search");
    startNavigation(() => router.push(address, { scroll: false }));
  };
  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!interactive) return;
    search(word.trim());
  };
  const clearSearch = () => {
    if (!interactive) return;
    focusAfterRender.current = "search";
    search("");
  };
  const chooseView = (next: PropertyListView) => {
    setView(next);
    if (!interactive) return;
    // The server ignores the view, so it needs no new render.
    window.history.replaceState(
      null,
      "",
      addressWith({ [names.view]: next === list.views[0] ? undefined : next }),
    );
    if (list.rememberView)
      try {
        window.localStorage.setItem(storageKey(), next);
      } catch {
        /* The choice is kept for this visit only. */
      }
  };

  const sale = status === "active";
  const searchOn = list.useSearch && list.showSearch;
  const currentSort = params.sort ?? list.defaultSort;
  const filtered = !!(params.q || params.filter || params.openhouse);
  const empty = items.length === 0;
  const nothingToShow = empty && !filtered;
  const showSort =
    !nothingToShow &&
    sale &&
    list.showSort &&
    !params.openhouse &&
    view !== "map" &&
    list.sortOptions.length > 1;
  const switchable = [...list.views];
  if (list.split && !switchable.includes("map")) switchable.push("map");
  const showViews = !nothingToShow && switchable.length > 1;
  const chip =
    params.filter &&
    !buttons.some((button) => button.namespace === params.filter)
      ? typeName(params.filter, typeNames)
      : undefined;
  const counts = list.showFilterCounts && !params.q && !params.openhouse;
  const announcement = empty
    ? filtered
      ? list.noResultsText
      : list.emptyText
    : countLine(list, items.length, total);
  const busy = (name: string) => navigating && control === name;

  const searchForm = searchOn && (
    <form
      role="search"
      method="get"
      className="pf-plist-search"
      onSubmit={submitSearch}
    >
      <label className="sr-only" htmlFor={`${id}-q`}>
        Søg efter bolig
      </label>
      <input
        ref={searchField}
        id={`${id}-q`}
        name={names.q}
        type="search"
        value={word}
        onChange={(event) => setWord(event.target.value)}
        placeholder={list.searchPlaceholder}
        readOnly={!interactive}
        aria-disabled={interactive ? undefined : true}
      />
      {params.filter && (
        <input type="hidden" name={names.filter} value={params.filter} />
      )}
      {params.sort && (
        <input type="hidden" name={names.sort} value={params.sort} />
      )}
      {params.openhouse && (
        <input type="hidden" name={names.openhouse} value="1" />
      )}
      {view !== list.views[0] && (
        <input type="hidden" name={names.view} value={view} />
      )}
      <span
        className={searchLook.className}
        aria-busy={busy("search") || undefined}
      >
        <RippleCtaButton
          type={interactive ? "submit" : "button"}
          config={{ ...searchLook.config, variant: undefined }}
        >
          {list.searchButtonText}
        </RippleCtaButton>
      </span>
    </form>
  );
  const filters = (buttons.length > 0 ||
    chip ||
    (sale && list.openHouseFilter)) && (
    <div className={`pf-plist-filters ${pill ? "pf-tabs-pill" : ""}`}>
      {buttons.length > 0 && (
        <div className="pf-filters" role="group" aria-label="Ejendomstype">
          {buttons.map((button) => (
            <ListFilterButton
              key={button.namespace}
              pressed={
                button.namespace === "all"
                  ? !params.filter
                  : params.filter === button.namespace
              }
              busy={busy(`filter:${button.namespace}`)}
              interactive={interactive}
              onClick={() =>
                chooseFilter(button.namespace, `filter:${button.namespace}`)
              }
            >
              {counts && button.count !== undefined
                ? `${button.displayName} (${button.count})`
                : button.displayName}
            </ListFilterButton>
          ))}
        </div>
      )}
      {chip && (
        <span className="pf-chip">
          <span>{chip}</span>
          <button
            type="button"
            aria-label={`Fjern filteret ${chip}`}
            aria-busy={busy("chip") || undefined}
            aria-disabled={interactive ? undefined : true}
            onClick={interactive && !busy("chip") ? removeChip : undefined}
          >
            ×
          </button>
        </span>
      )}
      {sale && list.openHouseFilter && (
        <ListFilterButton
          pressed={params.openhouse === true}
          busy={busy("openhouse")}
          interactive={interactive}
          onClick={toggleOpenHouse}
        >
          {list.openHouseLabel}
        </ListFilterButton>
      )}
    </div>
  );
  const tools = (showSort || showViews) && (
    <div className="pf-plist-tools">
      {showSort && (
        <div className="pf-plist-sort" inert={!interactive}>
          <SortCaseControl
            key={currentSort}
            name={`${id}-sort`}
            ariaLabel="Sorter boligerne"
            items={list.sortOptions.map((option) => ({
              ...option,
              selected: option.value === currentSort,
            }))}
            onSelected={(item: { value: string }) => chooseSort(item.value)}
          />
        </div>
      )}
      {showViews && (
        <div className="pf-plist-views" role="group" aria-label="Visning">
          {switchable.map((name) => (
            <span
              key={name}
              className={
                list.split && name === "map" && !list.views.includes("map")
                  ? "pf-plist-narrow"
                  : undefined
              }
            >
              <SortingButton
                label={VIEW_BUTTONS[name].label}
                isSelected={view === name}
                onClick={() => chooseView(name)}
              >
                <FontAwesomeIcon
                  className="size-6 flex"
                  icon={VIEW_BUTTONS[name].icon}
                />
              </SortingButton>
            </span>
          ))}
        </div>
      )}
    </div>
  );
  const reveal = list.lookSystem ? "" : undefined;
  const toolbar =
    searchForm || filters || tools ? (
      variant === 1 ? (
        <div className="pf-plist-panel pf-form" data-re-reveal={reveal}>
          {searchForm}
          {(filters || tools) && (
            <div className="pf-plist-toolbar">
              <div className="pf-plist-left" inert={!interactive || undefined}>
                {filters}
              </div>
              {tools}
            </div>
          )}
        </div>
      ) : (
        <div className="pf-plist-toolbar" data-re-reveal={reveal}>
          <div className="pf-plist-left">
            {searchForm}
            <div inert={!interactive || undefined}>{filters}</div>
          </div>
          {tools}
        </div>
      )
    ) : null;
  const statusLine = params.q ? (
    <div className="pf-plist-status" data-re-reveal={reveal}>
      <p className="pf-plist-searched">
        {list.searchHeading.replaceAll("{q}", params.q)}
      </p>
      <button
        type="button"
        className="pf-plist-clear"
        aria-disabled={interactive ? undefined : true}
        onClick={interactive ? clearSearch : undefined}
      >
        {list.clearSearchText}
      </button>
    </div>
  ) : null;
  const countEl = list.showCount && !empty && (
    <p className="pf-plist-count" aria-hidden="true" data-re-reveal={reveal}>
      {countLine(list, items.length, total)}
    </p>
  );
  const listView: Exclude<PropertyListView, "map"> | null =
    view === "map" ? null : view;
  const results = empty ? (
    <p className="pf-plist-empty" data-re-reveal={reveal}>
      {filtered ? list.noResultsText : list.emptyText}
    </p>
  ) : listView ? (
    <PropertyListCards
      items={items}
      view={listView}
      list={list}
      imageRatio={imageRatio}
    />
  ) : (
    <PropertyListMap
      items={items}
      total={total}
      list={list}
      imageRatio={imageRatio}
      illustration={mapIllustration}
    />
  );
  const showMore = list.loadMore && total > items.length;
  const more = showMore && (
    <div
      className="pf-plist-more"
      aria-busy={busy("more") || undefined}
      data-re-reveal={reveal}
    >
      <span className={loadMoreLook.className}>
        {mounted && interactive ? (
          <RippleCtaButtonComponent
            id={`${id}-more`}
            itemIndex={0}
            config={{ ...loadMoreLook.config, label: list.loadMoreButton.text }}
            onClick={loadMore}
          />
        ) : (
          <RippleCtaButtonComponent
            id={`${id}-more`}
            itemIndex={0}
            config={{
              ...loadMoreLook.config,
              label: list.loadMoreButton.text,
              link: interactive && moreHref ? moreHref : "#",
            }}
          />
        )}
      </span>
    </div>
  );
  return (
    <div
      ref={region}
      className={`pf-plist pf-plist-layout-${variant}`}
      role="region"
      aria-label={label}
      tabIndex={-1}
      data-section-key={sectionKey}
      data-view={view}
    >
      {toolbar}
      {statusLine}
      {list.countPosition === "above" && countEl}
      {list.split ? (
        <div
          className={`pf-plist-split ${view === "map" ? "pf-plist-split-map" : ""}`}
        >
          <div className="pf-plist-main">{results}</div>
          {!empty && view !== "map" && (
            <SideMap
              items={items}
              total={total}
              list={list}
              imageRatio={imageRatio}
              illustration={mapIllustration}
            />
          )}
        </div>
      ) : (
        results
      )}
      {(more || (list.countPosition === "below" && countEl)) && (
        <div className="pf-plist-paging">
          {more}
          {list.countPosition === "below" && countEl}
        </div>
      )}
      <p className="sr-only" aria-live="polite">
        {announcement}
      </p>
    </div>
  );
}

/** Liste og kortvisning: the map beside the list, mounted only on screens wide enough to show it. The illustration
 *  costs nothing, so it is drawn at once and the CSS hides it on narrow screens. */
function SideMap(props: {
  items: CaseData[];
  total: number;
  list: PropertyListSettings;
  imageRatio?: string;
  illustration?: boolean;
}) {
  const [wide, setWide] = useState(false);
  useEffect(() => {
    const query = window.matchMedia("(min-width: 1024px)");
    const update = () => setWide(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);
  return (
    <aside className="pf-plist-side" aria-label="Kort over boligerne">
      {(wide || props.illustration) && (
        <PropertyListMap {...props} withLinks={false} />
      )}
    </aside>
  );
}
